import { useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import PrivateRoute from './admin/components/PrivateRoute';
import AdminUpload from './admin/pages/AdminUpload';
import AdminReview from './admin/pages/AdminReview';
import AdminCards from './admin/pages/AdminCards';
import AdminCardDetail from './admin/pages/AdminCardDetail';
import AdminCardEdit from './admin/pages/AdminCardEdit';

const API_URL = process.env.REACT_APP_API_URL;

function App() {
    const [query, setQuery] = useState('');
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({ q1: '', q2: '', comment: '' });
    const [submitted, setSubmitted] = useState(false);

    const handleSend = async () => {
        if (!query.trim()) return;
        const userText = query.trim();
        setMessages(prev => [...prev, { type: 'user', text: userText }]);
        setQuery('');
        setLoading(true);
        setSubmitted(false);
        setFeedback({ q1: '', q2: '', comment: '' });

        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Query: userText }),
            });
            const parsed = await res.json();
            setMessages(prev => [...prev, { type: 'response', data: parsed }]);
        } catch (err) {
            setMessages(prev => [...prev, { type: 'error', text: 'Could not connect to API. Is the .NET API running?' }]);
        } finally {
            setLoading(false);
        }
    };

    const handleFeedback = (field, value) => {
        setFeedback(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmitFeedback = () => {
        const lastUserMsg = [...messages].reverse().find(m => m.type === 'user');
        console.log('Gate 3 Feedback:', { query: lastUserMsg?.text, feedback });
        setSubmitted(true);
    };

    const lastResponseIndex = messages.reduce((acc, m, i) => m.type === 'response' ? i : acc, -1);

    return (
        <div className="app">
            <header className="header">
                <div className="logo">U</div>
                <div>
                    <div className="brand">Udanchu</div>
                    <div className="tagline">Know your rights. Act with confidence.</div>
                </div>
                <span className="beta">Beta � Testing</span>
            </header>

            <div className="disclaimer">
                This is legal information, not legal advice. Consult a qualified lawyer for your specific situation.
            </div>

            <div className="chat-area">
                {messages.length === 0 && !loading && (
                    <div className="empty-state">
                        Describe your situation and Udanchu will tell you your rights.
                    </div>
                )}

                {messages.map((msg, i) => {
                    if (msg.type === 'user') {
                        return <div key={i} className="user-bubble">{msg.text}</div>;
                    }
                    if (msg.type === 'error') {
                        return <div key={i} className="error">{msg.text}</div>;
                    }
                    const response = msg.data;
                    const isLast = i === lastResponseIndex;
                    return (
                        <div key={i}>
                            <div className="response-card">
                                <div className="section">
                                    <div className="section-label answer">Answer</div>
                                    <div className="section-content bold">{response.short_answer}</div>
                                </div>

                                <div className="section">
                                    <div className="section-label explanation">Explanation</div>
                                    <div className="section-content">{response.explanation}</div>
                                </div>

                                <div className="section">
                                    <div className="section-label law">Law references</div>
                                    <div className="pills">
                                        {response.law_references?.map((ref, j) => (
                                            <span key={j} className="pill">{ref.act} � {ref.section}</span>
                                        ))}
                                    </div>
                                </div>

                                <div className="section">
                                    <div className="section-label steps">What to do next</div>
                                    {response.action_steps?.map((step, j) => (
                                        <div key={j} className="step">
                                            <div className="step-num">{j + 1}</div>
                                            <div className="step-text">{step}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="confidence-bar">
                                    <div className="conf-dot"></div>
                                    Confidence: {response.confidence} � {response.disclaimer}
                                </div>
                            </div>

                            {isLast && (!submitted ? (
                                <div className="feedback-card">
                                    <div className="feedback-title">Quick feedback � 3 questions</div>

                                    <div className="feedback-q">
                                        <div className="feedback-label">1. Did you understand this answer?</div>
                                        <div className="choice-row">
                                            {['Yes, fully', 'Partly', 'No'].map(opt => (
                                                <button
                                                    key={opt}
                                                    className={`choice ${feedback.q1 === opt ? 'selected-yes' : ''}`}
                                                    onClick={() => handleFeedback('q1', opt)}
                                                >{opt}</button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="feedback-q">
                                        <div className="feedback-label">2. Would you act on this guidance?</div>
                                        <div className="choice-row">
                                            {['Yes, I would', 'Maybe', 'No'].map(opt => (
                                                <button
                                                    key={opt}
                                                    className={`choice ${feedback.q2 === opt ? 'selected-yes' : ''}`}
                                                    onClick={() => handleFeedback('q2', opt)}
                                                >{opt}</button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="feedback-q">
                                        <div className="feedback-label">3. What was confusing or missing? (optional)</div>
                                        <textarea
                                            className="comment-box"
                                            placeholder="E.g. didn't understand step 3, wanted to know the deadline..."
                                            value={feedback.comment}
                                            onChange={e => handleFeedback('comment', e.target.value)}
                                        />
                                    </div>

                                    <button className="submit-btn" onClick={handleSubmitFeedback}>
                                        Submit feedback
                                    </button>
                                </div>
                            ) : (
                                <div className="submitted">
                                    Feedback recorded. Thank you.
                                </div>
                            ))}
                        </div>
                    );
                })}

                {loading && (
                    <div className="loading">Looking up your rights...</div>
                )}
            </div>

            <div className="input-bar">
                <input
                    className="input"
                    type="text"
                    placeholder="Describe your situation in your own words..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSend()}
                />
                <button className="send-btn" onClick={handleSend}>Send</button>
            </div>
        </div>
    );
}

function AppRouter() {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<App />} />
                <Route element={<PrivateRoute />}>
                    <Route path="/admin" element={<AdminUpload />} />
                    <Route path="/admin/review" element={<AdminReview />} />
                    <Route path="/admin/cards" element={<AdminCards />} />
                    <Route path="/admin/card/:id" element={<AdminCardDetail />} />
                    <Route path="/admin/card/:id/edit" element={<AdminCardEdit />} />
                </Route>
            </Routes>
        </BrowserRouter>
    );
}

export default AppRouter;
